import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ModuleHeader from "../../moduleHeader";
import { getAffirmationsHistory } from '../../../services/affirmationService';
import { getCurrentPackageUserModule } from '../../../services/api';

export default function Affirmation({ id, moduleId, isOwner }) {
  const navigate = useNavigate();
  const [affirmationsData, setAffirmationsData] = useState({});
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // Sayfa açıldığında direkt olumlamaya yönlendir
  useEffect(() => {
    navigate(`/${id}/${moduleId}/affirmation`);
  }, [id, moduleId, navigate]);

  // Paket ve kullanıcı bilgilerini çek (moduleId sabit 7 - Affirmation)
  useEffect(() => {
    if (!id) return;

    const fetchPackageData = async () => {
      try {
        const data = await getCurrentPackageUserModule(id, 7);
        setPackageData(data);
      } catch (error) {
        console.error("Paket verisi alınamadı:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageData();
  }, [id]);

  const fetchAffirmationsHistory = async () => {
    try {
      setDataLoading(true);
      const response = await getAffirmationsHistory();

      if (response && response.success && response.data) {
        setAffirmationsData(response.data);
      } else {
        setAffirmationsData({});
      }
    } catch (err) {
      setError("Geçmiş olumlamalar alınamadı");
      setAffirmationsData({});
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && packageData) {
      fetchAffirmationsHistory();
    }
  }, [loading, packageData]);

  const handleTodayAffirmation = () => {
    // Yeni route'a yönlendir
    navigate(`/${id}/${moduleId}/affirmation`);
  };

  // Loading durumunda loading ekranı göster
  if (loading || !packageData) {
    return (
      <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#e8e9ed]">
        <div className="flex flex-col items-center justify-center">
          <div className="relative mb-8">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60"
                style={{
                  animationDirection: "reverse",
                  animationDuration: "1.5s",
                }}
              ></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
            </div>
          </div>
          <p className="text-xl font-semibold text-gray-800 animate-pulse">
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
      <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
        {/* ModuleHeader */}
        {packageData && (
          <div className="relative z-30 w-full mb-2">
            <ModuleHeader
              userName={
                packageData.user?.first_name + " " + packageData.user?.last_name
              }
              subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
              title={packageData.package?.name}
              description={packageData.package?.description}
              backgroundImage={packageData.package?.image_url}
              onExpandedChange={setIsHeaderExpanded}
              showToggle={true}
              autoHeight={true}
            />
          </div>
        )}

        <div className="min-h-screen bg-white w-full px-6 py-8">
          {/* Bugünkü Olumlama Mesajını İçin Duyuru */}
          <button
            onClick={handleTodayAffirmation}
            className="w-full group relative overflow-hidden bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] hover:from-[#2F8894] hover:to-[#4A9BA6] text-white rounded-2xl mb-8 p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]">
            <div className="relative z-10 flex items-center justify-center gap-3">
              <div className="text-center">
                <p className="text-white text-lg font-bold mb-1">
                  Olumlamaya Git
                </p>
                <p className="text-white/80 text-sm">
                  Yeni günlük olumlamanı keşfet!
                </p>
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          </button>

          {/* Önceki Olumlamalarınız */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-gradient-to-b from-[#3DA7B5] to-[#5ABCC8] rounded-full"></div>
              <h2 className="text-gray-800 text-lg font-bold">
                Olumlama Geçmişi
              </h2>
            </div>

            {dataLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3DA7B5]"></div>
                <span className="ml-3 text-gray-500 text-sm">Yükleniyor...</span>
              </div>
            )}

            {error && !dataLoading && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <div className="text-red-600 text-sm font-medium">{error}</div>
              </div>
            )}

            {!dataLoading && !error && Object.keys(affirmationsData).length === 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                <div className="text-gray-400 text-4xl mb-3">📝</div>
                <div className="text-gray-600 text-sm font-medium">Henüz olumlama geçmişi bulunmuyor</div>
                <div className="text-gray-400 text-xs mt-1">İlk olumlamanızı oluşturmak için yukarıdaki butona tıklayın</div>
              </div>
            )}

            {!dataLoading && !error && Object.keys(affirmationsData).length > 0 && (
              <div className="space-y-3">
                {Object.entries(affirmationsData)
                  .sort(([a], [b]) => new Date(b) - new Date(a)) // Tarihe göre sırala (en yeni önce)
                  .filter(([date, affirmations]) => affirmations.length > 0) // Sadece olumlaması olan günleri göster
                  .map(([date, affirmations]) => {
                    const formattedDate = new Date(date).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    });

                    return (
                      <div key={date} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 p-4 group">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-[#3DA7B5] rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="text-xs text-gray-500 font-medium mb-2">{formattedDate}</div>
                            <p className="text-gray-800 text-sm leading-relaxed group-hover:text-[#3DA7B5] transition-colors">
                              {affirmations[0].content}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Alt boşluk */}
          <div className="h-20"></div>
        </div>
      </div>
    </div>
  );
}

